// @flow
import * as React from 'react';

import { useIntl, type MessageDescriptor } from 'react-intl';
import withStyles from '@plugins/material-ui/hocs/withStyles';
import Popover from '@material-ui/core/Popover';
import Chip from '@material-ui/core/Chip';
import Button from '@material-ui/core/Button';
import Typography from '@material-ui/core/Typography';
import AddIcon from '@material-ui/icons/Add';
import map from 'lodash/map';
import compact from 'lodash/compact';
import classnames from 'classnames';

import useGroupedTags from '~hooks/useGroupedTags';
import messages from './messages';
import type { Props } from './types';
import styles from './styles';

const EntryTagPopover = ({
  tags,
  anchorEl,
  onClose,
  onTagClick,
  onUnselectAll,
  isTagSelected,
  classes,
  className,
}: Props): React.Node => {
  const open = Boolean(anchorEl);

  const intl = useIntl();

  const takeMessage = React.useCallback(
    (message: ?MessageDescriptor | ?string): ?string =>
      typeof message === 'string'
        ? message
        : (message && intl.formatMessage(message)) || null,
    [intl.locale],
  );

  const tagsByCategories = useGroupedTags(tags);

  const renderedItems = React.useMemo(
    () =>
      compact(
        map(tagsByCategories, tagsByCategory => (
          <div key={tagsByCategory?.id} className={classes.wrapper}>
            <Typography variant="overline" color="primary">
              {tagsByCategory?.categoryName ||
                takeMessage(messages.withoutCategory)}
            </Typography>
            <div className={classes.items}>
              {compact(
                map(tagsByCategory.tags, tag => (
                  <Chip
                    key={tag?.uid}
                    label={tag?.data?.name?.text}
                    onClick={event => onTagClick(event, tag)}
                    onDelete={event => onTagClick(event, tag)}
                    deleteIcon={isTagSelected(tag) ? undefined : <AddIcon />}
                    color={isTagSelected(tag) ? 'primary' : 'secondary'}
                    className={classes.chip}
                  />
                )),
              )}
            </div>
          </div>
        )),
      ),
    [tagsByCategories, classes],
  );

  return (
    <Popover
      open={open}
      anchorEl={anchorEl}
      onClose={onClose}
      anchorOrigin={{
        vertical: 'bottom',
        horizontal: 'center',
      }}
      transformOrigin={{
        vertical: 'top',
        horizontal: 'center',
      }}
      className={classnames(classes.root, className)}
    >
      <div className={classes.wrapper}>
        {renderedItems}
        <Button
          color="primary"
          onClick={onUnselectAll}
          className={classes.cleanButton}
        >
          {takeMessage(messages.clearButton)}
        </Button>
      </div>
    </Popover>
  );
};

EntryTagPopover.defaultProps = {
  className: undefined,
};

export default withStyles<*, *, *>(styles, { name: 'EntryTagPopover' })(EntryTagPopover);
